const PENDING_RESULT_LINK_KEY = 'pendingResultLink';

export interface PendingResultLink {
  examId: string;
  classRoomID: string;
  /** The student account the mail went to. Absent in links from before it was added. */
  forUserID?: string;
}

/**
 * The results mail links to `/?results=<examId>&class=<classRoomID>&for=<userID>` (db/api/result-notifications.ts).
 * Like a join code, the target has to survive a login round-trip, so it is moved from the URL into localStorage and
 * the URL is cleaned; app/page.tsx consumes it once the account it is `for` is signed in, and keeps it while another
 * one is.
 */
export function stashResultLinkFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const examId = url.searchParams.get('results');
    const classRoomID = url.searchParams.get('class');
    const forUserID = url.searchParams.get('for') ?? undefined;
    if (!examId || !classRoomID) return;
    localStorage.setItem(PENDING_RESULT_LINK_KEY, JSON.stringify({ examId, classRoomID, forUserID }));
    url.searchParams.delete('results');
    url.searchParams.delete('class');
    url.searchParams.delete('for');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch (e) {}
}

/** Reads the stashed link without removing it — it stays while the wrong account is signed in. */
export function peekPendingResultLink(): PendingResultLink | null {
  try {
    const raw = localStorage.getItem(PENDING_RESULT_LINK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.examId !== 'string' || typeof parsed?.classRoomID !== 'string') return null;
    return {
      examId: parsed.examId,
      classRoomID: parsed.classRoomID,
      forUserID: typeof parsed.forUserID === 'string' && parsed.forUserID ? parsed.forUserID : undefined,
    };
  } catch (e) {
    return null;
  }
}

export function consumePendingResultLink(): PendingResultLink | null {
  const link = peekPendingResultLink();
  discardPendingResultLink();
  return link;
}

export function discardPendingResultLink(): void {
  try {
    localStorage.removeItem(PENDING_RESULT_LINK_KEY);
  } catch (e) {}
}

/** Whether `userID` may open the link: the account it was sent to, or anyone for links without `for`. */
export const isResultLinkFor = (link: PendingResultLink, userID: string) =>
  !link.forUserID || link.forUserID === userID;
